import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { CheckCircle, MapPin, MessageSquare, CreditCard, Loader2 } from "lucide-react";
import { lookupPincode } from "@/utils/pincode";
import {
  getDeliveryCharge,
  getDeliveryLabel,
  loadDeliveryPincode,
  saveDeliveryPincode,
} from "@/utils/delivery";

import { API_BASE as API } from "@/config/api";
import { openRazorpayCheckout, fetchRazorpayKeyId } from "@/utils/razorpay";

const indianStates = ["Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Delhi","Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal","Andaman and Nicobar Islands","Chandigarh","Dadra and Nagar Haveli and Daman and Diu","Jammu and Kashmir","Ladakh","Lakshadweep","Puducherry"];

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { cart, fetchCart } = useCart();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("cod");
  const [paymentError, setPaymentError] = useState("");
  const [razorpayKeyId, setRazorpayKeyId] = useState("");
  const [form, setForm] = useState({ name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: loadDeliveryPincode() });
  const [instructions, setInstructions] = useState("");
  const [errors, setErrors] = useState({});
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [pincodeMessage, setPincodeMessage] = useState("");
  const lastLookupPincode = useRef("");

  const stateOptions = useMemo(() => {
    if (form.state && !indianStates.includes(form.state)) {
      return [...indianStates, form.state];
    }
    return indianStates;
  }, [form.state]);

  useEffect(() => {
    fetchRazorpayKeyId().then(setRazorpayKeyId).catch(() => setRazorpayKeyId(""));
  }, []);

  useEffect(() => {
    const pincode = form.pincode.replace(/\D/g, "");
    if (pincode.length !== 6) {
      setPincodeLoading(false);
      setPincodeMessage("");
      lastLookupPincode.current = "";
      return undefined;
    }

    if (pincode === lastLookupPincode.current) {
      return undefined;
    }

    const timer = setTimeout(async () => {
      setPincodeLoading(true);
      setPincodeMessage("");
      try {
        const location = await lookupPincode(pincode);
        lastLookupPincode.current = pincode;
        setForm((prev) => ({
          ...prev,
          city: location.city,
          state: location.state,
        }));
        setPincodeMessage(`${location.city}, ${location.state}`);
        setErrors((prev) => {
          const next = { ...prev };
          delete next.pincode;
          delete next.city;
          delete next.state;
          return next;
        });
      } catch {
        lastLookupPincode.current = "";
        setPincodeMessage("");
        setErrors((prev) => ({ ...prev, pincode: "Could not find this pincode" }));
      } finally {
        setPincodeLoading(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [form.pincode]);

  const handlePincodeChange = (value) => {
    const pincode = value.replace(/\D/g, "").slice(0, 6);
    setForm((prev) => ({ ...prev, pincode }));
    saveDeliveryPincode(pincode);
    if (pincode.length < 6) {
      lastLookupPincode.current = "";
      setPincodeMessage("");
      setErrors((prev) => {
        const next = { ...prev };
        delete next.pincode;
        return next;
      });
    }
  };

  const delivery = getDeliveryCharge(form.pincode) ?? 0;
  const deliveryLabel = getDeliveryLabel(form.pincode);
  const pincodeReady = form.pincode.replace(/\D/g, "").length === 6;
  const grandTotal = cart.total + (pincodeReady ? delivery : 0);

  const validateForm = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.phone.trim() || !/^\d{10}$/.test(form.phone.trim())) e.phone = "Valid 10-digit phone required";
    if (!form.line1.trim()) e.line1 = "Address is required";
    if (!form.city.trim()) e.city = "City is required";
    if (!form.state) e.state = "State is required";
    if (!form.pincode.trim() || !/^\d{6}$/.test(form.pincode.trim())) e.pincode = "Valid 6-digit pincode required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handlePlaceOrder = async () => {
    if (!validateForm()) return;
    if (!pincodeReady) {
      setPaymentError("Enter a valid pincode to calculate delivery before placing your order.");
      return;
    }

    setPaymentError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/orders`, {
        address: form, payment_method: "cod",
        special_instructions: instructions
      }, { withCredentials: true });
      await fetchCart();
      setOrder(data);
      setStep(3);
    } catch (e) {
      setPaymentError(e.response?.data?.detail || "Failed to place order. Please try again.");
    } finally { setLoading(false); }
  };

  const handleRazorpayPayment = async () => {
    if (!validateForm()) return;
    if (!pincodeReady) {
      setPaymentError("Enter a valid pincode to calculate delivery before paying.");
      return;
    }

    const amountPaise = Math.round(grandTotal * 100);
    if (amountPaise < 100) {
      setPaymentError("Order total must be at least ₹1 to pay online.");
      return;
    }

    setPaymentError("");
    setLoading(true);

    try {
      const { data: razorpayOrder } = await axios.post(
        `${API}/create-order`,
        { amount: amountPaise, currency: "INR" },
        { withCredentials: true }
      );

      const keyId = razorpayOrder.key_id || razorpayKeyId || await fetchRazorpayKeyId();
      if (!keyId) {
        setPaymentError("Online payment is not configured. Please use Cash on Delivery.");
        return;
      }

      await openRazorpayCheckout({
        key: keyId,
        orderId: razorpayOrder.order_id,
        currency: razorpayOrder.currency,
        description: "Petal Pookie order",
        prefill: {
          name: form.name,
          contact: form.phone,
          email: user?.email || "",
        },
        onDismiss: () => setPaymentError("Payment cancelled."),
        onFailure: (response) => {
          const reason = response?.error?.description
            || response?.error?.reason
            || response?.error?.code
            || "Payment failed. Please try again.";
          setPaymentError(reason);
        },
        onSuccess: async (response) => {
          const { data } = await axios.post(
            `${API}/verify-payment`,
            {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              expected_amount: amountPaise,
              address: form,
              special_instructions: instructions,
            },
            { withCredentials: true }
          );
          await fetchCart();
          setOrder(data);
          setStep(3);
        },
      });
    } catch (e) {
      if (e.message !== "Payment cancelled") {
        setPaymentError(e.response?.data?.detail || e.message || "Could not complete payment. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = () => {
    if (paymentMethod === "razorpay") {
      handleRazorpayPayment();
      return;
    }
    handlePlaceOrder();
  };

  if (cart.items.length === 0 && !order) {
    return (
      <div className="min-h-screen bg-petal-beige flex items-center justify-center">
        <div className="text-center">
          <p className="font-playfair text-2xl text-petal-muted mb-4">Your cart is empty</p>
          <button onClick={() => navigate("/shop")} className="btn-primary">Shop Now</button>
        </div>
      </div>
    );
  }

  if (step === 3 && order) {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="min-h-screen bg-petal-beige flex items-center justify-center px-4" data-testid="order-success">
        <div className="max-w-lg w-full bg-white border border-petal-border p-10 text-center">
          <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
          <p className="font-dancing text-petal-gold text-2xl mb-2">Order Placed!</p>
          <h2 className="font-playfair text-3xl text-petal-olive-text mb-4">Thank You!</h2>
          <p className="font-outfit text-sm text-petal-muted mb-2">Your order has been placed successfully.</p>
          <p className="font-outfit text-sm font-semibold text-petal-olive mb-6">Order #{order.order_number}</p>
          <div className="bg-petal-beige-alt p-4 mb-6 text-left space-y-2">
            <p className="font-outfit text-xs uppercase tracking-widest text-petal-olive mb-3">Order Details</p>
            <div className="flex justify-between font-outfit text-sm">
              <span className="text-petal-muted">Items</span>
              <span className="font-medium">{order.items?.length}</span>
            </div>
            <div className="flex justify-between font-outfit text-sm">
              <span className="text-petal-muted">Total</span>
              <span className="font-semibold text-petal-olive-text">₹{order.total?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between font-outfit text-sm">
              <span className="text-petal-muted">Payment</span>
              <span className="font-medium uppercase">{order.payment_method === "razorpay" ? "Paid Online" : "COD"}</span>
            </div>
            <div className="flex justify-between font-outfit text-sm">
              <span className="text-petal-muted">Status</span>
              <span className="font-medium text-petal-olive capitalize">{order.status}</span>
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={() => navigate("/dashboard")} data-testid="view-orders-btn"
              className="flex-1 btn-primary text-xs py-3">Track Order</button>
            <button onClick={() => navigate("/shop")} className="flex-1 btn-outline text-xs py-3">Continue Shopping</button>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="min-h-screen bg-petal-beige" data-testid="checkout-page">
      <div className="bg-[#F5F0E8] py-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="font-playfair text-4xl sm:text-5xl text-petal-olive-text">Checkout</h1>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 md:px-8 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Delivery Address */}
            <div className="bg-white border border-petal-border p-6">
              <div className="flex items-center gap-3 mb-6">
                <MapPin className="w-5 h-5 text-petal-gold" />
                <h2 className="font-playfair text-xl text-petal-olive-text">Delivery Address</h2>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { field: "name", label: "Full Name", placeholder: "Your name", full: true },
                  { field: "phone", label: "Phone Number", placeholder: "10-digit mobile number", full: false },
                  { field: "line1", label: "Address Line 1", placeholder: "House no, Street", full: true },
                  { field: "line2", label: "Address Line 2 (Optional)", placeholder: "Area, Landmark", full: true },
                  { field: "city", label: "City", placeholder: "Auto-filled from pincode", full: false },
                ].map(({ field, label, placeholder, full }) => (
                  <div key={field} className={full ? "col-span-2" : "col-span-1"}>
                    <label className="block font-outfit text-xs uppercase tracking-widest text-petal-olive-text mb-1.5">{label}</label>
                    <input type="text" value={form[field]} onChange={e => setForm({ ...form, [field]: e.target.value })}
                      placeholder={placeholder} data-testid={`checkout-${field}`}
                      className={`w-full px-3 py-2.5 border bg-petal-beige font-outfit text-sm focus:outline-none focus:border-petal-olive transition-colors ${errors[field] ? "border-red-300" : "border-petal-border"}`} />
                    {errors[field] && <p className="font-outfit text-xs text-red-400 mt-1">{errors[field]}</p>}
                  </div>
                ))}
                <div className="col-span-1">
                  <label className="block font-outfit text-xs uppercase tracking-widest text-petal-olive-text mb-1.5">Pincode</label>
                  <div className="relative">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={form.pincode}
                      onChange={(e) => handlePincodeChange(e.target.value)}
                      placeholder="6-digit pincode"
                      data-testid="checkout-pincode"
                      className={`w-full px-3 py-2.5 border bg-petal-beige font-outfit text-sm focus:outline-none focus:border-petal-olive transition-colors ${errors.pincode ? "border-red-300" : "border-petal-border"}`}
                    />
                    {pincodeLoading && (
                      <Loader2 className="w-4 h-4 text-petal-olive absolute right-3 top-1/2 -translate-y-1/2 animate-spin" />
                    )}
                  </div>
                  {errors.pincode && <p className="font-outfit text-xs text-red-400 mt-1">{errors.pincode}</p>}
                  {!errors.pincode && pincodeMessage && (
                    <p className="font-outfit text-xs text-petal-olive mt-1">{pincodeMessage}</p>
                  )}
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <label className="block font-outfit text-xs uppercase tracking-widest text-petal-olive-text mb-1.5">State</label>
                  <select value={form.state} onChange={e => setForm({ ...form, state: e.target.value })} data-testid="checkout-state"
                    className={`w-full px-3 py-2.5 border bg-petal-beige font-outfit text-sm focus:outline-none focus:border-petal-olive ${errors.state ? "border-red-300" : "border-petal-border"}`}>
                    <option value="">Select State</option>
                    {stateOptions.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {errors.state && <p className="font-outfit text-xs text-red-400 mt-1">{errors.state}</p>}
                </div>
              </div>
            </div>

            {/* Special Instructions */}
            <div className="bg-white border border-petal-border p-6">
              <div className="flex items-center gap-3 mb-4">
                <MessageSquare className="w-5 h-5 text-petal-gold" />
                <h2 className="font-playfair text-xl text-petal-olive-text">Special Instructions</h2>
              </div>
              <textarea value={instructions} onChange={e => setInstructions(e.target.value)}
                placeholder="Any special packaging, message or delivery instructions..."
                rows={3} data-testid="special-instructions"
                className="w-full px-3 py-2.5 border border-petal-border bg-petal-beige font-outfit text-sm resize-none focus:outline-none focus:border-petal-olive" />
            </div>

            {/* Payment */}
            <div className="bg-white border border-petal-border p-6">
              <div className="flex items-center gap-3 mb-4">
                <CreditCard className="w-5 h-5 text-petal-gold" />
                <h2 className="font-playfair text-xl text-petal-olive-text">Payment Method</h2>
              </div>
              <div className="space-y-3">
                {[
                  {
                    id: "cod",
                    title: "Cash on Delivery (COD)",
                    description: "Pay when your order arrives",
                  },
                  {
                    id: "razorpay",
                    title: "Pay Online (UPI / Card / Netbanking)",
                    description: "Secure payment via Razorpay",
                  },
                ].map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => {
                      setPaymentMethod(option.id);
                      setPaymentError("");
                    }}
                    data-testid={`payment-method-${option.id}`}
                    className={`w-full border-2 p-4 flex items-center gap-3 text-left transition-colors ${
                      paymentMethod === option.id
                        ? "border-petal-olive bg-petal-beige-alt"
                        : "border-petal-border bg-white hover:border-petal-olive/40"
                    }`}
                  >
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                      paymentMethod === option.id ? "border-petal-olive" : "border-petal-border"
                    }`}>
                      {paymentMethod === option.id && <div className="w-2 h-2 rounded-full bg-petal-olive" />}
                    </div>
                    <div>
                      <p className="font-outfit text-sm font-semibold text-petal-olive-text">{option.title}</p>
                      <p className="font-outfit text-xs text-petal-muted">{option.description}</p>
                    </div>
                  </button>
                ))}
              </div>
              {paymentError && (
                <p className="font-outfit text-xs text-red-400 mt-3" data-testid="payment-error">{paymentError}</p>
              )}
            </div>
          </div>

          {/* Order Summary */}
          <div>
            <div className="bg-white border border-petal-border p-6 sticky top-24">
              <h2 className="font-playfair text-xl text-petal-olive-text mb-6">Order Summary</h2>
              <div className="space-y-3 mb-6">
                {cart.items.map(item => (
                  <div key={item.product_id} className="flex gap-3">
                    <img src={item.product?.images?.[0]} alt="" className="w-12 h-12 object-cover border border-petal-border shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-outfit text-xs text-petal-olive-text line-clamp-1">{item.product?.name}</p>
                      <p className="font-outfit text-xs text-petal-muted">Qty: {item.quantity}</p>
                    </div>
                    <p className="font-outfit text-sm font-medium text-petal-olive-text whitespace-nowrap">₹{item.item_total?.toLocaleString()}</p>
                  </div>
                ))}
              </div>
              <div className="border-t border-petal-border pt-4 space-y-2">
                <div className="flex justify-between font-outfit text-sm">
                  <span className="text-petal-muted">Subtotal</span>
                  <span>₹{cart.total.toLocaleString()}</span>
                </div>
                <div className="flex justify-between font-outfit text-sm">
                  <span className="text-petal-muted">Delivery</span>
                  <span>{pincodeReady ? `₹${delivery}` : "—"}</span>
                </div>
                {pincodeReady && deliveryLabel && (
                  <p className="font-outfit text-xs text-petal-muted text-right">{deliveryLabel}</p>
                )}
                <div className="flex justify-between font-outfit text-base font-semibold border-t border-petal-border pt-2">
                  <span className="text-petal-olive-text">Total</span>
                  <span className="text-petal-olive-text" data-testid="checkout-total">₹{grandTotal.toLocaleString()}</span>
                </div>
              </div>
              <button onClick={handleCheckout} disabled={loading || !pincodeReady} data-testid="place-order-btn"
                className="w-full btn-primary py-4 mt-6 disabled:opacity-50">
                {loading
                  ? (paymentMethod === "razorpay" ? "Processing Payment..." : "Placing Order...")
                  : (paymentMethod === "razorpay" ? "Pay Now" : "Place Order (COD)")}
              </button>
              {!pincodeReady && (
                <p className="font-outfit text-xs text-petal-muted mt-2 text-center">Enter pincode to enable checkout</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
